import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ThemeProvider } from 'next-themes'
import { Direction } from 'radix-ui'
import type { ReactNode } from 'react'
import { useState } from 'react'
import { Toaster } from '@/components/ui/sonner'
import { PrintHost } from '@/features/printing/print-host'
import { TooltipProvider } from '@/components/ui/tooltip'
import { meQueryKey } from '@/features/auth/use-me'
import { ApiError } from '@/lib/api-error'
import { useDir } from '@/lib/use-dir'
import { NewVersionPrompt } from './new-version-prompt'

function createClient() {
  // Any 401 means the session ended (expired, idle, revoked): drop "me" so guards send us to /login.
  const onError = (error: unknown) => {
    if (error instanceof ApiError && error.status === 401) client.setQueryData(meQueryKey, null)
  }
  const client: QueryClient = new QueryClient({
    queryCache: new QueryCache({ onError }),
    mutationCache: new MutationCache({ onError }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 2,
      },
    },
  })
  return client
}

export function Providers({ children }: { children: ReactNode }) {
  const [client] = useState(createClient)
  // Radix primitives (tabs, menus, selects, sliders…) lay out and navigate left-to-right unless told otherwise.
  const dir = useDir()
  return (
    <Direction.Provider dir={dir}>
      <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
        <QueryClientProvider client={client}>
          <TooltipProvider delayDuration={300}>
            <PrintHost>{children}</PrintHost>
            <Toaster position="top-center" richColors />
            <NewVersionPrompt />
          </TooltipProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </Direction.Provider>
  )
}
